import React, { createContext, useContext } from 'react';
import { Deal, Document, Task } from '../types';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { sampleDeals } from '../data/sampleDeals';
import { sampleDocuments } from '../data/sampleDocuments';
import { sampleTasks } from '../data/sampleTasks';

interface DealsContextType {
  deals: Deal[];
  documents: Document[];
  tasks: Task[];
  addDeal: (deal: Deal) => void;
  updateDeal: (deal: Deal) => void;
  addDocument: (document: Document) => void;
  updateDocument: (document: Document) => void;
  deleteDocument: (documentId: string) => void;
  shareDocument: (documentId: string, email: string) => void;
  addTask: (task: Task) => void;
  updateTask: (task: Task) => void;
}

const DealsContext = createContext<DealsContextType | undefined>(undefined);

export function DealsProvider({ children }: { children: React.ReactNode }) {
  const [deals, setDeals] = useLocalStorage<Deal[]>('deals', sampleDeals);
  const [documents, setDocuments] = useLocalStorage<Document[]>('documents', sampleDocuments);
  const [tasks, setTasks] = useLocalStorage<Task[]>('tasks', sampleTasks);

  const addDeal = (deal: Deal) => {
    setDeals([...deals, deal]);
  };

  const updateDeal = (updatedDeal: Deal) => {
    setDeals(deals.map(deal => deal.id === updatedDeal.id ? updatedDeal : deal));
  };

  const addDocument = (document: Document) => {
    setDocuments([...documents, document]);
  };

  const updateDocument = (updatedDocument: Document) => {
    setDocuments(documents.map(doc => doc.id === updatedDocument.id ? updatedDocument : doc));
  };

  const deleteDocument = (documentId: string) => {
    setDocuments(documents.filter(doc => doc.id !== documentId));
  };

  const shareDocument = (documentId: string, email: string) => {
    // In a real app, this would integrate with an email service
    console.log(`Sharing document ${documentId} with ${email}`);
  };

  const addTask = (task: Task) => {
    setTasks([...tasks, task]);
  };

  const updateTask = (updatedTask: Task) => {
    setTasks(tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
  };

  return (
    <DealsContext.Provider value={{
      deals,
      documents,
      tasks,
      addDeal,
      updateDeal,
      addDocument,
      updateDocument,
      deleteDocument,
      shareDocument,
      addTask,
      updateTask
    }}>
      {children}
    </DealsContext.Provider>
  );
}

export function useDeals() {
  const context = useContext(DealsContext);
  if (context === undefined) {
    throw new Error('useDeals must be used within a DealsProvider');
  }
  return context;
}